import Button from "@/components/Button/index.js";
import EFormComponent from "@/components/EForm/index.jsx";
import { ToastContext } from "@/context/toast/index";
import useEForm from "@/hooks/use-eform/index";
import { useLocation, useNavigate, useParams } from "@/lib/router-dom/index";
import apiEclips from "@/services/api/eclips";
import apiPackages from "@/services/api/packages.js";
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import { useContext, useEffect, useState } from "react";
import EFormRenderer from "@/components/EFormV2/EFormRenderer";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const PackageEFormPage = () => {
	const { goTo } = useNavigate();
	const { currentState, currentPath } = useLocation();
	const { eclipId } = useParams();
	const { addToastMessage } = useContext(ToastContext);

	const defaultRedirectUri = "/package";
	const [dataElementMeta, setDataElementMeta] = useState(null);

	const currentFolder = currentState?.currentFolder
	const currentPackage = currentState?.currentPackage
	const currentDataElement = currentState?.dataElement

	let breadcrumbItem =[] ;

	if (currentPath?.includes('/package')) {
		breadcrumbItem = [
			{
				label: '',
				link: '/'
			},
			{
				label: 'Package Sharing',
				link: '/package'
			},
			...(currentFolder?.name
				? [
					{
						label: currentFolder.name,
						link: `/package/folder/${currentFolder.id}`
					}
				]
				: []),
			{
				label: currentPackage?.name,
				link: `/package/details/${currentPackage?.id}`
			},
			{
				label: currentDataElement?.elementName,
				link: ''
			},
		];
	} else {
		breadcrumbItem = [
			{ label: '',
				link: `/`,
			},
			{
				label: 'Package Sharing',
				link: `${currentPath?.includes('/package')? '/package' : '/eclip'}`
			},
			{
				label: `Data E-form`,
				link: ``
			},
		]
	}

	const {
		payload,
		setPayload,
		isDirty,
		setIsDirty,
		getFullJson,
		extractCombinedSchema,
		getSchemaWithoutAttachment,
		isReadOnly,
		setIsReadOnly,
	} = useEForm();

	const [schema, setSchema] = useState(null);
	const [isValidForm, setIsValidForm] = useState(false);

	const dataSource = {
		eform: "EFORM",
	}

	useEffect(() => {
		if (!currentState?.dataElement) {

			if (currentState?.version === 'v2') {
				goTo(defaultRedirectUri);
			} else {
				goTo(-1)
			}

			return;
		}

		const isReadOnly = currentState?.isReadOnly ? true : false;
		setIsReadOnly(isReadOnly);

		const rawDataElement = currentState.dataElement;

		setDataElementMeta({
			dataElementId: rawDataElement.dataElementId,
			sourceFilename: "",
			source: dataSource.eform,
			parent_node_id: rawDataElement.parent_node_id,
			parent_node_type: rawDataElement.parent_node_type,
			recipientOrgId: rawDataElement.recipientOrgId,
			onBehalfOrgId: rawDataElement.onBehalfOrgId,
		});

		let { savedPayload: payload, elementSchema: schema } = rawDataElement;

		if (Array.isArray(payload)) {
			payload = payload[0];
		}

		const plainSchema = extractCombinedSchema(schema, payload);
		const schemaWithoutAttachment = getSchemaWithoutAttachment(plainSchema);

		setSchema(schemaWithoutAttachment);
		setPayload(getFullJson(schemaWithoutAttachment, payload));
	}, [currentState]);

	useEffect(() => {
		if (payload) {
			validateSchema();
		}
	}, [payload]);

	function validateSchema() {
		const validate = ajv.compile(schema);
		const { attachments, ...payloadToBeValidate } = payload;

		const isPayloadValid = validate(payloadToBeValidate);
		setIsValidForm(isPayloadValid);
		return isPayloadValid;
	}

	const props = {
		schema,
		payload,
		setPayload,
		isDirty,
		isReadOnly,
	};

	function onCancelBtnClick() {
		goTo(-1);
	}

	async function onSubmitBtnClick() {
		const validateResult = validateSchema();
		setIsDirty();

		if (!validateResult || !dataElementMeta) {
			return;
		}

		const requestBody = {
			dataElements: [
				{
					...dataElementMeta,
					payload: [payload],
				},
			],
		};

		let error;
		if (currentState.version === 'v1')
			[error] = await apiEclips.update(
				eclipId,
				{
					...requestBody,
					recipients: currentState.recipients
				}
			);
		else
			[error] = await apiPackages.updatePackage(
				eclipId,
				requestBody
			);

		if (error) {
			addToastMessage("Failed to submit eform", "error");
			return;
		}

		goTo(-1);
	}

	if (!currentState || !payload) {
		return <></>;
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbItem} />
			<div className="font-size-xxl font-weight-bold">Data E-Form</div>
			<div className="eform-form-container mt-1">
				<div className="flex-box flex-space-between mb-1">
					<div
						data-testid="eform-page-form-title"
						className="font-color-grey-100 font-size-xl font-weight-bold"
					>
						{schema.title ?? schema.description}
					</div>

					<div className="font-color-grey-60 font-size-m font-weight-bold">
						* Refers to mandatory fields
					</div>
				</div>
				<div className="eform-header-border"></div>
				<div className="eform-form">
					<EFormRenderer {...props} />
				</div>

				<div className="DS__layout__footer">
					<div className="layout-default__sticky-actions">
						<div className="layout-default__sticky-actions__left">
							<Button
								data-testid="eform-page-cancel-btn"
								text="Cancel"
								sizing="Medium"
								full
								noborder
								maxWidth="154"
								submit={() => onCancelBtnClick()}
							/>
						</div>

						<div className="layout-default__sticky-actions__right">
							<Button
								data-testid="eform-page-submit-btn"
								text="Submit"
								sizing="Medium"
								full
								maxWidth="154"
								disabled={!isValidForm || isReadOnly}
								submit={() => onSubmitBtnClick()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default PackageEFormPage;
